/**
 * WhatsApp Integration Utility
 * Handles WhatsApp Business API integration and messaging
 */

class WhatsAppIntegration {
  constructor() {
    this.businessNumber = '+1234567890'; // Replace with actual business number
    this.apiUrl = 'https://api.whatsapp.com/send';
    this.webUrl = 'https://web.whatsapp.com/send';
  }

  /**
   * Open WhatsApp chat with pre-filled message
   */
  openChat(phoneNumber = null, message = '', options = {}) {
    const targetNumber = phoneNumber || this.businessNumber;
    const encodedMessage = encodeURIComponent(message);
    
    // Detect if mobile or desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    let whatsappUrl;
    if (isMobile) {
      // Use WhatsApp app on mobile
      whatsappUrl = `${this.apiUrl}?phone=${targetNumber}&text=${encodedMessage}`;
    } else {
      // Use WhatsApp Web on desktop
      whatsappUrl = `${this.webUrl}?phone=${targetNumber}&text=${encodedMessage}`;
    }

    // Open in new window/tab
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    
    // Track the interaction if analytics are available
    this.trackWhatsAppInteraction(targetNumber, message, options);
  }

  /**
   * Generate support message template
   */
  generateSupportMessage(userInfo = {}, issueType = 'general', customMessage = '') {
    const { name, email, orderNumber } = userInfo;
    
    let message = `Hello! I'm contacting you from the Gifted Solutions website.\n\n`;
    
    if (name) {
      message += `Name: ${name}\n`;
    }
    
    if (email) {
      message += `Email: ${email}\n`;
    }
    
    if (orderNumber) {
      message += `Order Number: ${orderNumber}\n`;
    }
    
    message += `\nIssue Type: ${this.getIssueTypeLabel(issueType)}\n\n`;
    
    if (customMessage) {
      message += `Message: ${customMessage}\n\n`;
    } else {
      message += `I need assistance with: ${this.getDefaultMessageForIssueType(issueType)}\n\n`;
    }
    
    message += `Please help me resolve this issue. Thank you!`;
    
    return message;
  }

  /**
   * Generate product inquiry message
   */
  generateProductInquiry(productInfo = {}, userInfo = {}) {
    const { productName, productId, category } = productInfo;
    const { name, email } = userInfo;
    
    let message = `Hello! I'm interested in your products.\n\n`;
    
    if (name) {
      message += `Name: ${name}\n`;
    }
    
    if (email) {
      message += `Email: ${email}\n`;
    }
    
    if (productName) {
      message += `\nProduct: ${productName}\n`;
    }
    
    if (productId) {
      message += `Product ID: ${productId}\n`;
    }
    
    if (category) {
      message += `Category: ${category}\n`;
    }
    
    message += `\nI would like to know more about:\n`;
    message += `• Pricing and availability\n`;
    message += `• Technical specifications\n`;
    message += `• Shipping options\n`;
    message += `• Bulk pricing (if applicable)\n\n`;
    message += `Please provide more information. Thank you!`;
    
    return message;
  }

  /**
   * Generate order inquiry message
   */
  generateOrderInquiry(orderInfo = {}, userInfo = {}) {
    const { orderNumber, orderDate, issueType } = orderInfo;
    const { name, email } = userInfo;
    
    let message = `Hello! I have a question about my order.\n\n`;
    
    if (name) {
      message += `Name: ${name}\n`;
    }
    
    if (email) {
      message += `Email: ${email}\n`;
    }
    
    if (orderNumber) {
      message += `Order Number: ${orderNumber}\n`;
    }
    
    if (orderDate) {
      message += `Order Date: ${orderDate}\n`;
    }
    
    message += `\nInquiry Type: ${issueType || 'General Order Question'}\n\n`;
    message += `Please help me with my order. Thank you!`;
    
    return message;
  }

  /**
   * Generate technical support message
   */
  generateTechnicalSupport(techInfo = {}, userInfo = {}) {
    const { productName, issueDescription, errorMessage, projectType } = techInfo;
    const { name, email } = userInfo;
    
    let message = `Hello! I need technical support.\n\n`;
    
    if (name) {
      message += `Name: ${name}\n`;
    }
    
    if (email) {
      message += `Email: ${email}\n`;
    }
    
    if (productName) {
      message += `Product: ${productName}\n`;
    }
    
    if (projectType) {
      message += `Project Type: ${projectType}\n`;
    }
    
    if (issueDescription) {
      message += `\nIssue Description: ${issueDescription}\n`;
    }
    
    if (errorMessage) {
      message += `Error Message: ${errorMessage}\n`;
    }
    
    message += `\nI would appreciate your technical assistance. Thank you!`;
    
    return message;
  }

  /**
   * Get issue type label
   */
  getIssueTypeLabel(issueType) {
    const labels = {
      'general': 'General Inquiry',
      'product': 'Product Question',
      'order': 'Order Support',
      'technical': 'Technical Support',
      'billing': 'Billing Issue',
      'shipping': 'Shipping Question',
      'return': 'Return/Refund',
      'complaint': 'Complaint'
    };
    
    return labels[issueType] || 'General Inquiry';
  }

  /**
   * Get default message for issue type
   */
  getDefaultMessageForIssueType(issueType) {
    const messages = {
      'general': 'General information about your products and services',
      'product': 'Product specifications, pricing, and availability',
      'order': 'Order status, shipping, and delivery information',
      'technical': 'Technical support for products and projects',
      'billing': 'Billing questions and payment issues',
      'shipping': 'Shipping options and delivery tracking',
      'return': 'Return policy and refund process',
      'complaint': 'Service or product complaint'
    };
    
    return messages[issueType] || 'General assistance';
  }

  /**
   * Track WhatsApp interaction for analytics
   */
  trackWhatsAppInteraction(phoneNumber, message, options = {}) {
    try {
      // Track with Google Analytics if available
      if (typeof gtag !== 'undefined') {
        gtag('event', 'whatsapp_contact', {
          'event_category': 'engagement',
          'event_label': options.issueType || 'general',
          'value': 1
        });
      }
      
      // Track with custom analytics
      if (window.analytics && typeof window.analytics.track === 'function') {
        window.analytics.track('WhatsApp Contact', {
          phoneNumber: phoneNumber,
          issueType: options.issueType || 'general',
          messageLength: message.length,
          timestamp: new Date().toISOString()
        });
      }
      
      console.log('WhatsApp interaction tracked:', {
        phoneNumber,
        issueType: options.issueType,
        timestamp: new Date()
      });
    } catch (error) {
      console.warn('Failed to track WhatsApp interaction:', error);
    }
  }

  /**
   * Check if WhatsApp is available on device
   */
  isWhatsAppAvailable() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile || true; // WhatsApp Web works on desktop too
  }

  /**
   * Get business hours status
   */
  isBusinessHours() {
    const now = new Date();
    const hour = now.getHours();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Business hours: Monday-Friday 9AM-6PM, Saturday 10AM-4PM
    if (day === 0) return false; // Sunday closed
    if (day === 6) return hour >= 10 && hour < 16; // Saturday 10-4
    return hour >= 9 && hour < 18; // Monday-Friday 9-6
  }

  /**
   * Get estimated response time
   */
  getEstimatedResponseTime() {
    if (this.isBusinessHours()) {
      return 'Usually responds within 30 minutes';
    } else {
      return 'Usually responds within 2-4 hours';
    }
  }

  /**
   * Format phone number for WhatsApp
   */
  formatPhoneNumber(phoneNumber) {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Add country code if not present
    if (!cleaned.startsWith('1') && cleaned.length === 10) {
      return '1' + cleaned;
    }
    
    return cleaned;
  }
}

// Create singleton instance
const whatsappIntegration = new WhatsAppIntegration();

export default whatsappIntegration;

// Export utility functions
export const {
  openChat,
  generateSupportMessage,
  generateProductInquiry,
  generateOrderInquiry,
  generateTechnicalSupport,
  isWhatsAppAvailable,
  isBusinessHours,
  getEstimatedResponseTime
} = whatsappIntegration;
